import { escapeRegExp } from 'lodash';
import { showInfoToast } from 'core/components';
import { observable, computed, action } from 'mobx';
import { ENTITY_TYPES, MAX_ALLOWED_SELECTED_OCI_COMPARTMENTS } from '@kentik/ui-shared/hybrid/constants';

import CloudMapCollection from './CloudMapCollection';

const {
  REGION,
  SUBNET,
  COMPARTMENT,
  NAT_GATEWAY,
  INTERNET_GATEWAY,
  LOCAL_PEERING_GATEWAY,
  VIRTUAL_CLOUD_NETWORK,
  DYNAMIC_ROUTING_GATEWAY,
  DYNAMIC_ROUTING_GATEWAY_ATTACHMENT
} = ENTITY_TYPES.get('oci');

class OciCloudMapCollection extends CloudMapCollection {
  // will keep list of account ids that used for loading this collection
  // we need this variable to compare against user settings in $hybridMap disposer to decide either map topology need to be refreshed or no
  currentLoadedCompartmentIds = [];

  collectionManagedEntityTypes = [REGION];

  @observable.ref
  initialTopology = { Hierarchy: { Regions: [] }, Entities: {}, Links: [] };

  @observable
  /** [{type, value}] */
  highLightedNodes = [];

  @observable
  isLoading = false;

  searchableIdPaths = ['device_id', 'name', 'device_name', 'location', 'id'];

  searchableCIDRPaths = ['cidrBlock'];

  get url() {
    return '/api/ui/topology/cloud-hierarchy/oci';
  }

  get fetchMethod() {
    return 'post';
  }

  get Entities() {
    return this.initialTopology?.Entities ?? {};
  }

  get Summary() {
    return this.initialTopology.Summary;
  }

  get Links() {
    return this.initialTopology?.Links ?? [];
  }

  @action
  async fetch(options = {}) {
    if (options?.data?.compartmentIds?.length > MAX_ALLOWED_SELECTED_OCI_COMPARTMENTS) {
      options.data.compartmentIds = options.data.compartmentIds.slice(0, MAX_ALLOWED_SELECTED_OCI_COMPARTMENTS);

      showInfoToast(`Current compartment limit is ${MAX_ALLOWED_SELECTED_OCI_COMPARTMENTS}.`, {
        title: 'OCI topology',
        timeout: 4000
      });

      this.parent?.settingsModel?.set('ociCompartmentIds', options.data.compartmentIds);
    }

    const fetchedCompartmentIds = options?.data?.compartmentIds;

    if (fetchedCompartmentIds && Array.isArray(fetchedCompartmentIds)) {
      this.currentLoadedCompartmentIds = fetchedCompartmentIds;
    }

    return super.fetch(options);
  }

  deserialize(response) {
    // capture the initial topology response
    this.initialTopology = response;

    // get a comprehensive list of models for the collection
    const models = [
      { entityType: REGION },
      { entityType: SUBNET },
      { entityType: NAT_GATEWAY },
      { entityType: INTERNET_GATEWAY },
      { entityType: LOCAL_PEERING_GATEWAY },
      { entityType: VIRTUAL_CLOUD_NETWORK },
      { entityType: DYNAMIC_ROUTING_GATEWAY },
      { entityType: DYNAMIC_ROUTING_GATEWAY_ATTACHMENT }
    ].reduce((acc, entityConfig) => acc.concat(this.getActiveEntities({ ...entityConfig, allowUnlinked: true })), []);

    const modelsWithSearchableData = this.addSearchableDataToModels({
      entities: response.Hierarchy.Regions,
      models,
      additionalTypes: ['compartmentIds', 'tenancyIds']
    });
    return modelsWithSearchableData;
  }

  @computed
  get filterStateGroups() {
    const query = this.filterState || '';

    return query
      .split(',')
      .map((term) => term.trim())
      .reduce(
        (acc, term) => {
          if (term === '') {
            return acc;
          }

          if (this.isValidIP(term)) {
            acc.cidrs.push(term);
            return acc;
          }

          if (term.includes('ocid1.tenancy.oc1')) {
            acc.tenancyIds.push(term);
            return acc;
          }

          if (term.includes('ocid1.compartment.oc1')) {
            acc.compartmentIds.push(term);
            return acc;
          }

          acc.ids.push(term);

          return acc;
        },
        {
          tenancyIds: [],
          compartmentIds: [],
          ids: [],
          cidrs: []
        }
      );
  }

  filter(query) {
    this.filterState = query || '';
    const { filterStateGroups } = this;

    const discreteFilters = Object.keys(filterStateGroups).reduce((acc, groupName) => {
      const values = filterStateGroups[groupName];
      if (values.length > 0) {
        if (groupName === 'tenancyIds') {
          return acc.concat({
            type: groupName,
            values,
            fn: (model) => values.find((value) => model.searchableData.tenancyIds.find((id) => id.includes(value)))
          });
        }

        if (groupName === 'compartmentIds') {
          return acc.concat({
            type: groupName,
            values,
            fn: (model) => values.find((value) => model.searchableData.compartmentIds.find((id) => id.includes(value)))
          });
        }

        if (groupName === 'ids') {
          return acc.concat({
            type: groupName,
            values,
            fn: (model) =>
              values.find((value) =>
                model.searchableData.ids.find((id) => new RegExp(escapeRegExp(value), 'i').test(id))
              )
          });
        }

        if (groupName === 'cidrs') {
          return acc.concat({
            type: groupName,
            values,
            fn: (model) =>
              values.find((value) => model.searchableData.cidrs.find((cidr) => this.isInSubnet(value, cidr)))
          });
        }
      }

      return acc;
    }, []);

    this.models.replace(this.get());

    if (discreteFilters.length > 0) {
      discreteFilters.forEach((filter) => {
        this.models.replace(this.models.filter((model) => filter.fn(model)));
      });
    }

    this.sort();

    return this.models;
  }

  isRegionHierarchyEmpty(region) {
    return !region?.[VIRTUAL_CLOUD_NETWORK]?.length && !region?.[SUBNET]?.length;
  }

  @computed
  get topology() {
    const { Hierarchy } = this.initialTopology;
    const platformHierarchy = {};

    platformHierarchy.Regions = Hierarchy.Regions.map((region) =>
      this.hydrateHierarchy({ entity: region, entityType: REGION })
    ).filter((network) => !this.isRegionHierarchyEmpty(network));

    return {
      ...this.initialTopology,
      Hierarchy: platformHierarchy
    };
  }

  getSearchableCIDRList(data = {}) {
    return Object.values(data).flat();
  }

  getSearchableDataFromEntity({ entity, type = 'id', clean = false }) {
    if (type === 'compartmentIds') {
      return this.cleanSearchableData([entity?.compartmentId]);
    }

    if (type === 'tenancyIds') {
      return this.cleanSearchableData([entity?.tenancyId]);
    }

    return super.getSearchableDataFromEntity({ entity, type, clean });
  }

  findEntityById(id, entityType = null) {
    if (entityType) {
      return this.Entities[entityType][id] ?? null;
    }
    for (const tEntityType in this.Entities) {
      if (this.Entities[tEntityType][id]) {
        return this.Entities[tEntityType][id];
      }
    }

    console.warn(`unable to locate ${id} in topology entities.`);
    return null;
  }

  /**
   * @param name
   * @param entityType
   * @param additionalChecks - key value pair that should match in entity -
   * example: {vcnId : 'asd'} will also check that entity has property vcnId with value 'asd'
   * @returns {unknown|null}
   */
  findEntityByName(name, entityType = null, additionalChecks = {}) {
    if (entityType) {
      return (
        Object.values(this.Entities[entityType]).find(
          (entity) =>
            entity.displayName === name &&
            // every additional check key should exist in entity
            Object.keys(additionalChecks).every(
              (additionalCheckProperty) => entity[additionalCheckProperty] === additionalChecks[additionalCheckProperty]
            )
        ) ?? null
      );
    }

    for (const loopEntityType in this.Entities) {
      if (this.Entities[loopEntityType]) {
        const entity = Object.values(this.Entities[loopEntityType]).find(
          (entityTypeEntity) =>
            entityTypeEntity.displayName === name &&
            // every additional check key should exist in entity
            Object.keys(additionalChecks).every(
              (additionalCheckProperty) =>
                entityTypeEntity[additionalCheckProperty] === additionalChecks[additionalCheckProperty]
            )
        );

        if (entity) {
          return entity;
        }
      }
    }

    console.warn(`unable to locate entity with displayName ${name} in topology entities.`);
    return null;
  }

  @computed
  get ociCompartmentEntities() {
    const ociCompartments = this.topology.Entities[COMPARTMENT] ?? {};
    return Object.values(ociCompartments);
  }
}

export default OciCloudMapCollection;
